import SectionLabel from "@/components/SectionLabel";
import { ExternalLink, BookMarked, Compass, ScrollText } from "lucide-react";

const TRUSTED = [
  { name: "The Holy See", url: "https://www.vatican.va/content/vatican/en.html" },
  { name: "Vatican News", url: "https://www.vaticannews.va/en.html" },
  { name: "CBCP News", url: "https://cbcpnews.net/cbcpnews/" },
  {
    name: "Catechism of the Catholic Church",
    url: "https://www.vatican.va/archive/ENG0015/_INDEX.HTM",
  },
  { name: "USCCB Daily Readings", url: "https://bible.usccb.org/daily-bible-reading" },
  { name: "National Catholic Register", url: "https://www.ncregister.com/" },
  { name: "McGrath Institute for Church Life", url: "https://mcgrath.nd.edu/" },
  { name: "PAGASA · Weather", url: "https://www.pagasa.dost.gov.ph/" },
  { name: "Internet Encyclopedia of Philosophy", url: "https://iep.utm.edu/" },
  { name: "New Advent · Summa Theologiae", url: "https://www.newadvent.org/summa/" },
];

const SYNOD = [
  {
    name: "Final Reports",
    url: "https://www.synod.va/en/news/final-document-of-the-second-session-of-the-xvi-assembly.html",
    sub: "XVI Ordinary General Assembly · 2024",
  },
  {
    name: "Towards the Assemblies 2027 — 2028",
    url: "https://www.synod.va/en/highlights/towards-october-2028.html",
    sub: "Synod implementation phase",
  },
  {
    name: "CBCP Resources for the Synod",
    url: "https://cbcponline.net/synod/",
    sub: "Catholic Bishops' Conference of the Philippines",
  },
];

const ENCYCLICALS = [
  {
    name: "Magnifica Humanitas",
    sub: "Pope Leo XIV · Apostolic Exhortation",
    url: "https://www.vatican.va/content/leo-xiv/en/apost_exhortations.html",
  },
  {
    name: "Dilexi Te",
    sub: "Pope Leo XIV · 2025",
    url: "https://www.vatican.va/content/leo-xiv/en/apost_exhortations.html",
  },
  {
    name: "Dilexit Nos",
    sub: "Pope Francis · 2024 · On the Sacred Heart",
    url: "https://www.vatican.va/content/francesco/en/encyclicals/documents/20241024-enciclica-dilexit-nos.html",
  },
  {
    name: "Fratelli Tutti",
    sub: "Pope Francis · 2020 · Fraternity & Social Friendship",
    url: "https://www.vatican.va/content/francesco/en/encyclicals/documents/papa-francesco_20201003_enciclica-fratelli-tutti.html",
  },
  {
    name: "Laudato Si'",
    sub: "Pope Francis · 2015 · Care for Our Common Home",
    url: "https://www.vatican.va/content/francesco/en/encyclicals/documents/papa-francesco_20150524_enciclica-laudato-si.html",
  },
  {
    name: "Lumen Fidei",
    sub: "Pope Francis · 2013 · On Faith",
    url: "https://www.vatican.va/content/francesco/en/encyclicals/documents/papa-francesco_20130629_enciclica-lumen-fidei.html",
  },
];

function LinkCard({ name, url, sub }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`resource-link-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group block border border-parchment-400 bg-parchment-50 p-5 hover:border-terracotta hover:-translate-y-0.5 transition-all duration-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-serif text-lg text-cocoa-900 leading-tight group-hover:text-terracotta transition-colors">
            {name}
          </div>
          {sub && (
            <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mt-1.5 font-sans">
              {sub}
            </div>
          )}
        </div>
        <ExternalLink
          size={14}
          className="text-cocoa-700 group-hover:text-terracotta transition-colors flex-shrink-0 mt-1.5"
        />
      </div>
    </a>
  );
}

function Group({ icon: Icon, kicker, title, intro, items, cols = 3 }) {
  const gridClass =
    cols === 2
      ? "sm:grid-cols-2"
      : cols === 4
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : "sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div className="mt-16">
      <div className="flex items-center gap-3 mb-4">
        <Icon className="text-terracotta" size={20} strokeWidth={1.4} />
        <span className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-sans">
          {kicker}
        </span>
      </div>
      <h3 className="font-serif text-3xl md:text-4xl text-cocoa-900 leading-tight max-w-2xl">
        {title}
      </h3>
      {intro && (
        <p className="mt-3 font-sans text-cocoa-700 font-light max-w-2xl text-[15px] leading-relaxed">
          {intro}
        </p>
      )}
      <div className={`mt-8 grid ${gridClass} gap-4`}>
        {items.map((it) => (
          <LinkCard key={it.name} {...it} />
        ))}
      </div>
    </div>
  );
}

export default function PilgrimLibrary() {
  return (
    <section id="library" className="py-24 md:py-36 bg-parchment-200 grain relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(143,151,121,0.08),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Wells of Wisdom"
          title="Pilgrim Library"
          intro="A curated companion of Catholic teaching, Magisterium, synodal documents, and public service references — gathered for parishioners and seekers on the way."
        />

        <Group
          icon={Compass}
          kicker="Trusted Catholic & Public Resources"
          title="For prayer, study, news & public service."
          intro="Each link opens in a new tab and leads to the official source."
          items={TRUSTED}
          cols={3}
        />

        <Group
          icon={ScrollText}
          kicker="The Synod on Synodality"
          title="Walking together as one Church."
          intro="The Synod's final documents and the Philippine Bishops' resources for parish discernment."
          items={SYNOD}
          cols={3}
        />

        <Group
          icon={BookMarked}
          kicker="Encyclicals & Apostolic Exhortations"
          title="The voice of the Popes."
          intro="From recent Magisterial documents to landmark teachings on faith, love, fraternity, and the care of our common home."
          items={ENCYCLICALS}
          cols={3}
        />

        <p className="mt-16 text-center text-[11px] uppercase tracking-[0.28em] text-sage-dark font-sans italic max-w-2xl mx-auto">
          ✦ The parish does not own or operate the linked sites. Each source is provided as an
          aid to formation, prayer, and pastoral discernment.
        </p>
      </div>
    </section>
  );
}
